import React, {useRef, useEffect} from 'react';
import {Conversation} from '../utils/types';
import MessageGroup from './MessageGroup';

interface MessageAreaProps {
    conversation: Conversation | null;
    toggleMessageExpansion: (conversationId: number, messageId: number) => void;
    isLoading: boolean;
}

const MessageArea: React.FC<MessageAreaProps> = ({
                                                     conversation,
                                                     toggleMessageExpansion,
                                                     isLoading
                                                 }) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messageAreaRef = useRef<HTMLDivElement>(null);
    const prevMessagesLengthRef = useRef<number>(0);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        if (conversation?.messages) {
            const currentMessagesLength = conversation.messages.length;

            if (currentMessagesLength > prevMessagesLengthRef.current) {
                scrollToBottom();

                setTimeout(scrollToBottom, 100);

                if (messageAreaRef.current?.parentElement) {
                    setTimeout(() => {
                        if (messageAreaRef.current?.parentElement) {
                            messageAreaRef.current.parentElement.scrollTop =
                                messageAreaRef.current.parentElement.scrollHeight;
                        }
                    }, 150);
                }
            }

            prevMessagesLengthRef.current = currentMessagesLength;
        }
    }, [conversation?.messages]);

    useEffect(() => {
        if (!isLoading) {
            scrollToBottom();
        }
    }, [isLoading]);

    const renderGroupedMessages = () => {
        if (!conversation) return null;

        const groupedMessages: { [key: string]: any[] } = {};
        conversation.messages.forEach(message => {
            const groupId = message.groupId || message.id;
            if (!groupedMessages[groupId]) {
                groupedMessages[groupId] = [];
            }
            groupedMessages[groupId].push(message);
        });

        return Object.keys(groupedMessages).map(groupId => (
            <MessageGroup
                key={groupId}
                messages={groupedMessages[groupId]}
                conversationId={conversation.id}
                toggleMessageExpansion={toggleMessageExpansion}
                isLoading={isLoading}
            />
        ));
    };

    return (
        <div
            ref={messageAreaRef}
            className="flex-1 p-4"
        >
            <div className="container mx-auto max-w-4xl h-full overflow-y-auto">

                {conversation && conversation.messages.length > 0 ? (
                    renderGroupedMessages()
                ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">
                        {conversation
                            ? "There are no messages yet. Start a conversation by entering a message below."
                            : "Select a conversation or create a new one to get started."}
                    </div>
                )}
                <div ref={messagesEndRef}/>
            </div>
        </div>
    );
};

export default MessageArea;